import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Divider,
  Modal,
  Switch,
  TextField,
  Typography
} from '@mui/material';

import LoadingButton from '../../../../../components/buttons/Button';
import useSearchableSelect from '../../../../../components/hooks/useSearchableSelect';

import {
  CreateCompanyVectorPrefBIZcoreTD,
  UpdateCompanyVectorPrefBIZcoreTD
} from '../../../../../redux/actions/business/core/tradeDefencesActions';
import { 
  GetCompaniesDropdownBIZcore 
} from '../../../../../redux/actions/business/core/companiesActions';
import { 
  GetComplianceVectorsDropdownBIZcoreTD 
} from '../../../../../redux/actions/business/core/tradeDefencesActions';

import {
  CLEAR_COMPANIES_DROPDOWN_BIZ_CORE,
  CLEAR_COMPLIANCE_VECTORS_DROPDOWN_BIZ_CORE_TD
} from '../../../../../redux/actionTypes';

import global from "../../../../../global.scss";

const MODES = { CREATE: 1, EDIT: 2 };

const Data = {
  Company: null,
  Vector: null,
  IsActive: true,
};

const dropdownPaperProps = {
  sx: {
    backgroundColor: "rgba(44, 45, 47, 0.95)",
    backdropFilter: "blur(18.3px)",
    border: "1px solid rgba(80, 80, 82, 0.72)",
    borderRadius: "12px",
  }
};

const CompanyVectorPrefModal = ({
  open,
  setOpen,
  title,
  update,
  handleGet
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(Data);
  const [mode, setMode] = useState(MODES.CREATE);

  // ── Searchable select: Companies ──────────────────────────────────────────
  const {
    results: companyResults,
    loading: companySearchLoading,
    handleSearch: handleCompanySearch,
    reset: resetCompanySearch,
  } = useSearchableSelect(
    GetCompaniesDropdownBIZcore,
    (state) => {
      const list = state["business.core"].companiesDropdownList;
      if (list?.records && Array.isArray(list.records)) {
        return list.records;
      }
      return [];
    },
    null,
    { limit: 10, formatQuery: (q) => `&search=${q}` }
  );

  // ── Searchable select: Compliance Vectors ─────────────────────────────────
  const {
    results: vectorResults,
    loading: vectorSearchLoading,
    handleSearch: handleVectorSearch,
    reset: resetVectorSearch,
  } = useSearchableSelect(
    GetComplianceVectorsDropdownBIZcoreTD,
    (state) => {
      const list = state["business.core.tradeDefence"].complianceVectorsDropdownList;
      if (list?.records && Array.isArray(list.records)) {
        return list.records;
      }
      return [];
    },
    null,
    { limit: 10, formatQuery: (q) => `&search=${q}` }
  );

  const handleClose = () => {
    setOpen(false);
    setFormData(Data);
    setMode(MODES.CREATE);
    dispatch({ type: CLEAR_COMPANIES_DROPDOWN_BIZ_CORE });
    dispatch({ type: CLEAR_COMPLIANCE_VECTORS_DROPDOWN_BIZ_CORE_TD });
  };

  useEffect(() => {
    try {
      if (open) {
        resetCompanySearch();
        resetVectorSearch();
        if (update.defaultMode === MODES.CREATE) {
          setMode(MODES.CREATE);
          setFormData(Data);
        } else if (update.defaultMode === MODES.EDIT) {
          setMode(MODES.EDIT);
          setFormData({
            Company: update.ref_company_id ? { id: update.ref_company_id } : null,
            Vector: update.ref_vector_id || null,
            IsActive: update.is_active !== undefined ? update.is_active : true,
          });
        } else {
          toast.error("Invalid modal");
          handleClose();
        }
      }
    } catch (err) {
      console.log(err);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSubmit = () => {
    if (mode === MODES.CREATE) {
      const obj = {
        ref_company_id: formData.Company?.company_id || null,
        ref_vector_id: formData.Vector?.id || null,
        is_active: formData.IsActive,
      };
      dispatch(CreateCompanyVectorPrefBIZcoreTD(obj, setLoading)).then(() => {
        handleGet(setLoading).then(() => {
          handleClose();
        });
      });
    } else if (mode === MODES.EDIT) {
      const obj = {
        is_active: formData.IsActive,
      };
      dispatch(UpdateCompanyVectorPrefBIZcoreTD(update.id, obj, setLoading)).then(() => {
        handleGet(setLoading).then(() => {
          handleClose();
        });
      });
    } else {
      toast.error("Invalid modal");
      handleClose();
    }
  };

  const isSubmitDisabled = () => {
    if (mode === MODES.CREATE) {
      return !(formData.Company && formData.Vector);
    }
    return false;
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="parent-modal-title"
      aria-describedby="parent-modal-description"
    >
      <Box className="flexCenterCenterRow"
        width={"100vw"} height={"100vh"}>
        <Box maxWidth={"sm"} className="modal">
          <Box width={"100%"}
            sx={{ padding: "15px" }}>
            <Typography variant='h6'
              sx={{ color: global["primary"] }} mb={"20px"}>
              {title}
            </Typography>

            {/* ── Company ── */}
            <Box mb={"15px"}>
              <Autocomplete
                fullWidth
                options={Array.isArray(companyResults) ? companyResults : []}
                loading={companySearchLoading}
                disabled={mode === MODES.EDIT}
                value={formData.Company}
                onInputChange={(e, value, reason) => {
                  if (reason === 'input') handleCompanySearch(value);
                }}
                onChange={(e, value) => setFormData({ ...formData, Company: value || null })}
                getOptionLabel={(option) =>
                  typeof option === 'string'
                  ? option
                  : (option.company_name || option.name || option.id || "NA")
                }
                isOptionEqualToValue={(option, value) => {
                  if (!value) return false;
                  const valueId = typeof value === 'string' ? value : value?.id;
                  return option.id === valueId;
                }}
                componentsProps={{ paper: dropdownPaperProps }}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Typography variant="body2" color={"white"}>
                      {option.company_name || option.name || option.id || "NA"}
                    </Typography>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Company"
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {companySearchLoading ? <CircularProgress color="inherit" size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Box>

            {/* ── Compliance Vector ── */}
            <Box mb={"15px"}>
              <Autocomplete
                fullWidth
                options={Array.isArray(vectorResults) ? vectorResults : []}
                loading={vectorSearchLoading}
                disabled={mode === MODES.EDIT}
                value={formData.Vector}
                onInputChange={(e, value, reason) => {
                  if (reason === 'input') handleVectorSearch(value);
                }}
                onChange={(e, value) => setFormData({ ...formData, Vector: value || null })}
                getOptionLabel={(option) =>
                  typeof option === 'string'
                  ? option
                  : (option.name || option.id || "NA")
                }
                isOptionEqualToValue={(option, value) => {
                  if (!value) return false;
                  const valueId = typeof value === 'string' ? value : value?.id;
                  return option.id === valueId;
                }}
                componentsProps={{ paper: dropdownPaperProps }}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Typography variant="body2" color={"water"}>
                      {option.name || option.id || "NA"}
                    </Typography>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Compliance Vector"
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {vectorSearchLoading ? <CircularProgress color="inherit" size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Box>

            {/* ── Is Active ── */}
            <Box mb={"15px"} className="flexCenterSBRow">
              <Typography variant="body2">Is active</Typography>
              <Switch
                checked={formData.IsActive === true}
                onChange={(e) => setFormData({ ...formData, IsActive: e.target.checked })}
              />
            </Box>
          </Box>
          <Divider
            sx={{ backgroundColor: "rgba(44, 45, 47, 1)" }} />
          <Box className="flexCenterFERow" sx={{ gap: "20px", margin: "15px" }}>
            <Button variant='text'
              onClick={handleClose}>
              Cancel
            </Button>
            <LoadingButton
              variant='contained'
              loading={loading}
              disabled={isSubmitDisabled()}
              onClick={handleSubmit}>
              Submit
            </LoadingButton>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default CompanyVectorPrefModal;
